

import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import { useAuth } from '../../Common/Configurations/AuthContext';

const CustomerShareModal = ({ show, onHide, promotion, user }) => {
    const { refreshUserProfile } = useAuth();
    const [activeTab, setActiveTab] = useState('customer');
    const [isSending, setIsSending] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    
    // State for Customers
    const [customers, setCustomers] = useState([]);
    const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');

    // State for Agents
    const [agents, setAgents] = useState([]);
    const [selectedAgentIds, setSelectedAgentIds] = useState([]);
    const [agentSearchTerm, setAgentSearchTerm] = useState('');

    useEffect(() => {
        if (!show) return;
        setActiveTab('customer');
        setCustomers([]);
        setSelectedCustomerIds([]);
        setCustomerSearchTerm('');
        setAgents([]);
        setSelectedAgentIds([]);
        setAgentSearchTerm('');
    }, [show]);

    useEffect(() => {
        const getData = async () => {
            if (!show || !user?.cid) return;

            // Loading state is now set in handleTabChange, but needed for the initial load
            if (!isLoadingData) setIsLoadingData(true);

            try {
                if (activeTab === 'customer') {
                    if (customers.length === 0 || customerSearchTerm) {
                        let url = `${config.GETALLCUSTOMERSBYCOMPANYID}/${user.cid}`;
                        if (customerSearchTerm) url += `?search=${encodeURIComponent(customerSearchTerm)}`;
                        
                        const customerList = await apiService.getSingle(url);
                        setCustomers(customerList || []);
                        if (!customerSearchTerm) {
                            setSelectedCustomerIds((customerList || []).map(c => c.customerId));
                        }
                    }
                } else if (activeTab === 'agent') {
                    if (agents.length === 0 || agentSearchTerm) {
                        let url = `${config.GETALLAGENTBYCOMPANY}/${user.cid}`;
                        if (agentSearchTerm) url += `?search=${encodeURIComponent(agentSearchTerm)}`;

                        const agentList = await apiService.getSingle(url);
                        setAgents(agentList || []);
                        if (!agentSearchTerm) {
                            setSelectedAgentIds((agentList || []).map(a => a.companyId));
                        }
                    }
                }
            } catch (error) {
                toast.error(`Could not load ${activeTab} list.`);
            } finally {
                setIsLoadingData(false);
            }
        };

        const debounceTimer = setTimeout(getData, 300);
        return () => clearTimeout(debounceTimer);
    }, [show, user?.cid, customerSearchTerm, agentSearchTerm, activeTab]);

    const handleSelection = (id, isChecked) => {
        if (activeTab === 'customer') {
            setSelectedCustomerIds(prev => isChecked ? [...prev, id] : prev.filter(sid => sid !== id));
        } else {
            setSelectedAgentIds(prev => isChecked ? [...prev, id] : prev.filter(sid => sid !== id));
        }
    };

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        if (activeTab === 'customer') {
            setSelectedCustomerIds(isChecked ? customers.map(c => c.customerId) : []);
        } else {
            setSelectedAgentIds(isChecked ? agents.map(a => a.companyId) : []);
        }
    };

    // --- FIX 1: Dedicated Tab Change Handler ---
    const handleTabChange = (tab) => {
        if (activeTab === tab) return;
        setIsLoadingData(true); // Set loading immediately
        setActiveTab(tab);
    };

    const handleSendPromotion = async () => {
        const selectedCount = activeTab === 'customer' ? selectedCustomerIds.length : selectedAgentIds.length;
        if (selectedCount === 0) {
            toast.warn(`Please select at least one ${activeTab}.`);
            return;
        }
        setIsSending(true);
        try {
            let recipientPhones;
            if (activeTab === 'customer') {
                recipientPhones = customers
                    .filter(c => selectedCustomerIds.includes(c.customerId))
                    .map(c => c.phoneNumber);
            } else {
                recipientPhones = agents
                    .filter(a => selectedAgentIds.includes(a.companyId))
                    .map(a => a.phoneNumber);
            }

            const formattedPhones = recipientPhones
                .filter(phone => phone)
                .map(phone => phone.startsWith('91') ? phone : '91' + phone);

            if (formattedPhones.length === 0) {
                toast.error(`None of the selected ${activeTab}s have a valid phone number.`);
                setIsSending(false);
                return;
            }

            const payload = {
                customerPhoneNumbers: formattedPhones,
                content: promotion.content,
                imageUrl: promotion.imageSrc,
                companyId: user.cid,
            };

            await apiService.create(config.SENDPROMOTIONWHATSAPP, payload);
            await refreshUserProfile();
            onHide();
        } catch (error) {
           const errorMessage = error.response?.data?.message || "Failed to send promotion.";
           toast.error(errorMessage);
        } finally {
            setIsSending(false);
        }
    };
    
    const currentList = activeTab === 'customer' ? customers : agents;
    const selectedIds = activeTab === 'customer' ? selectedCustomerIds : selectedAgentIds;
    const searchTerm = activeTab === 'customer' ? customerSearchTerm : agentSearchTerm;
    const setSearchTerm = activeTab === 'customer' ? setCustomerSearchTerm : setAgentSearchTerm;
    const isAllSelected = currentList.length > 0 && selectedIds.length === currentList.length;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Send "{promotion?.title}"</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row">
                    <div className="col-md-7">
                        <ul className="nav nav-tabs nav-tabs-custom nav-success mb-3">
                            <li className="nav-item"><a className={`nav-link ${activeTab === 'customer' ? 'active' : ''}`} href="#!" onClick={() => handleTabChange('customer')}>Customers</a></li>
                            <li className="nav-item"><a className={`nav-link ${activeTab === 'agent' ? 'active' : ''}`} href="#!" onClick={() => handleTabChange('agent')}>Agents</a></li>
                        </ul>
                        <h6>Select {activeTab === 'customer' ? 'Customers' : 'Agents'} ({selectedIds.length} Selected)</h6>
                        <input type="text" className="form-control my-2" placeholder={`Search ${activeTab}s...`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                        
                        {/* --- FIX 2: Move "Select All" checkbox here --- */}
                        <div className="d-flex align-items-center mb-2">
                            <input className="form-check-input me-2" type="checkbox" id="promoSelectAll" onChange={handleSelectAll} checked={isAllSelected} disabled={currentList.length === 0} />
                            <label className="form-check-label fw-semibold" htmlFor="promoSelectAll">Select All</label>
                        </div>

                        <div className="table-responsive border rounded" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                            {isLoadingData ? (
                                <div className="text-center p-5">Loading...</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                                        <tr>
                                            <th style={{ width: '50px' }}></th>
                                            <th>Name</th>
                                            <th>Contact</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentList.map(item => {
                                            const id = activeTab === 'customer' ? item.customerId : item.companyId;
                                            const name = activeTab === 'customer' ? item.customerName : item.companyName;
                                            return (
                                                <tr key={id}>
                                                    <td><div className="form-check"><input className="form-check-input" type="checkbox" checked={selectedIds.includes(id)} onChange={e => handleSelection(id, e.target.checked)} /></div></td>
                                                    <td>{name}</td>
                                                    <td>{item.phoneNumber}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                    <div className="col-md-5">
                        <h6>WhatsApp Preview</h6>
                        <div className="whatsapp-preview-box border rounded p-3" style={{backgroundColor: '#e5ddd5'}}>
                            <div className="bg-white rounded p-2">
                                <img src={promotion?.imageSrc} alt="Preview" className="img-fluid rounded mb-2" />
                                <div className="caption"><p className="mb-0 small">{promotion?.content}</p></div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                 <Button variant="secondary" onClick={onHide}>Cancel</Button>
                 <Button variant="success" onClick={handleSendPromotion} disabled={isSending || isLoadingData || selectedIds.length === 0}>
                    <i className="ri-whatsapp-line me-1"></i>
                    {isSending ? 'Sending...' : `Send to ${selectedIds.length} Recipients`}
                 </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CustomerShareModal;